import React, { useState, useEffect } from 'react';
import { AuthState, SpendableCalculation } from '../types/app';
import { YNABBudget, YNABCategory/*, YNABTransaction, YNABScheduledTransaction */ } from '../types/ynab';
import { ynabService } from '../services/ynabService';
import { calculationService } from '../services/calculationService';
import { authService } from '../services/authService';
import { BudgetSelector } from './BudgetSelector';
import { SpendableGrid } from './SpendableGrid';
import { SettingsModal } from './SettingsModal';
import './Dashboard.css';
import log from 'loglevel';

interface DashboardProps {
  authState: AuthState;
  onAuthStateUpdate: (authState: AuthState) => void;
}

const CATEGORY_STORAGE_KEY = 'spendable.selectedCategories';
export const Dashboard: React.FC<DashboardProps> = ({ authState, onAuthStateUpdate }) => {
  const [budgets, setBudgets] = useState<YNABBudget[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<YNABBudget | null>(null);
  const [spendableCalculations, setSpendableCalculations] = useState<SpendableCalculation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [categoryGroups, setCategoryGroups] = useState<any[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(CATEGORY_STORAGE_KEY) || '["miscellaneous", "Cosmetics and other", "Groceries"]');
    } catch {
      return ["miscellaneous", "Cosmetics and other", "Groceries"];
    }
  });

  useEffect(() => {
    loadBudgets();
  }, [authState.accessToken]);

  useEffect(() => {
    if (selectedBudget && authState.accessToken) {
      loadBudgetData();
    }
  }, [selectedBudget, authState.accessToken, selectedCategoryIds]);

  const loadBudgets = async () => {
    if (!authState.accessToken) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const budgetsResponse = await ynabService.getBudgets(authState.accessToken);
      setBudgets(budgetsResponse.budgets);
      
      // Auto-select budget if there's a saved one or use default
      const savedBudgetId = authState.budgetId;
      let budgetToSelect = budgetsResponse.default_budget;
      
      if (savedBudgetId) {
        const savedBudget = budgetsResponse.budgets.find(b => b.id === savedBudgetId);
        if (savedBudget) {
          budgetToSelect = savedBudget;
        }
      }
      
      if (budgetToSelect) {
        setSelectedBudget(budgetToSelect);
        if (budgetToSelect.id !== authState.budgetId) {
          authService.setBudgetId(budgetToSelect.id);
          onAuthStateUpdate({
            ...authState,
            budgetId: budgetToSelect.id,
          });
        }
      }
    } catch (err) {
      console.error('Error loading budgets:', err);
      setError('Failed to load budgets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBudgetData = async () => {
    if (!selectedBudget || !authState.accessToken) return;

    try {
      setIsLoading(true);
      setError(null);

      // Calculate tomorrow's date for filtering transactions
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowDateString = tomorrow.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      // Load categories, transactions, and scheduled transactions in parallel
      const [categoriesResponse, transactionsResponse, scheduledResponse] = await Promise.all([
        ynabService.getCategories(selectedBudget.id, authState.accessToken),
        ynabService.getTransactions(selectedBudget.id, authState.accessToken, tomorrowDateString),
        ynabService.getScheduledTransactions(selectedBudget.id, authState.accessToken),
      ]);

      setCategoryGroups(categoriesResponse.category_groups);

      // Flatten categories from category groups
      const allCategories: YNABCategory[] = categoriesResponse.category_groups
        .flatMap(group => group.categories);

      // Get selected category names
      const selectedCategoryNames = allCategories
        .filter(cat => selectedCategoryIds.length === 0 || selectedCategoryIds.includes(cat.id))
        .map(cat => cat.name);

      // Calculate spendable amounts
      const calculations = calculationService.calculateSpendableAmounts(
        allCategories,
        transactionsResponse.transactions,
        scheduledResponse.scheduled_transactions,
        selectedCategoryNames
      );

      setSpendableCalculations(calculations);
    } catch (err) {
      console.error('Error loading budget data:', err);
      setError('Failed to load budget data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBudgetSelect = (budget: YNABBudget) => {
    setSelectedBudget(budget);
    authService.setBudgetId(budget.id);
    onAuthStateUpdate({
      ...authState,
      budgetId: budget.id,
    });
  };

  const handleSettingsSave = (selected: string[]) => {
    setSelectedCategoryIds(selected);
    log.info('Selected categories updated:', selected);
    localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(selected));
    // Reload budget data to reflect new selection
    loadBudgetData();
  };

  const handleRefresh = () => {
    if (selectedBudget) {
      loadBudgetData();
    }
  };

  if (isLoading && !selectedBudget) {
    return (
      <div className="dashboard">
        <div className="loading">
          <h2>Loading budgets...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadBudgets} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {selectedBudget && (
        <div className="dashboard-content">
          {isLoading ? (
            <div className="loading">
              <p>Loading budget data...</p>
            </div>
          ) : (
            <SpendableGrid calculations={spendableCalculations} />
          )}
        </div>
      )}
      
      <div className="dashboard-controls">
        <BudgetSelector
          budgets={budgets}
          selectedBudget={selectedBudget}
          onBudgetSelect={handleBudgetSelect}
        />
        {selectedBudget && (
          <>
            <button onClick={handleRefresh} className="refresh-button" disabled={isLoading}>
              {isLoading ? '⏳' : '🔄'}
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="settings-button"
              style={{ backgroundColor: '#1976d2', color: 'white', marginLeft: '8px' }}
              disabled={isLoading}
            >
              Settings
            </button>
          </>
        )}
        <div className="version-number">ver. {__APP_VERSION__}</div>
      </div>

      {showSettings && (
        <SettingsModal
          categoryGroups={categoryGroups}
          selectedCategoryIds={selectedCategoryIds}
          onSave={handleSettingsSave}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};
